import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { OperateursService } from '../../../data/intervenant/services/operateurs/operateurs.service';
import { SessionContext } from '../../../core/services/config/app.settings';
import { Organisme } from '../../../data/intervenant/models/organisme.model';
import { HistoriqueModification } from '../../../data/intervenant/models/historique-modification.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as moment from 'moment/moment';
import { DatePicker } from '../../../shared/models/date-picker.model';
import { LoaderService } from '../../../core/services/loader/loader.service';
import { switchMap } from 'rxjs/operators';
import { Table, TableModule } from 'primeng/table';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { MultiSelect } from 'primeng/multiselect';

@Component({
    selector: 'app-historique-modification',
    templateUrl: './historique-modification.component.html',
    styleUrls: ['./historique-modification.component.scss'],
    imports: [ReactiveFormsModule, FaIconComponent, TableModule, TranslateModule, MultiSelect, FormsModule],
    standalone: true
})
export class HistoriqueModificationComponent implements OnInit {
    /**
     * Getter id organisme
     */
    get idOrganisme(): number {
        return SessionContext.get('idOrganisme');
    }

    /**
     * Getter id opérateur
     */
    get idIntervenant(): number {
        return SessionContext.get('idIntervenant');
    }

    /**
     * Liste des historiques
     */
    historique: HistoriqueModification[] = [];

    /**
     * Organisme
     */
    organismes: Organisme[] = [];

    /**
     * Id operateur
     */
    idOperateur: number;

    /**
     * Liste des colonnes
     */
    columns = [
        { field: 'dateModification', label: 'Date de modification', visible: true, filter: 'date', width: '250px', frozen: true },
        { field: 'raisonSocialeOperateur', label: 'Raison sociale', visible: true, filter: 'text', width: '250px', frozen: true },
        { field: 'cviOperateur', label: 'CVI', visible: false, filter: 'text', width: '250px', frozen: false },
        { field: 'typeModification', label: 'Type', visible: true, filter: 'modificationType', width: '250px', frozen: false },
        { field: 'informationModifiee', label: 'Information modifiée', visible: true, filter: 'text', width: '250px', frozen: false },
        { field: 'oldValue', label: 'Ancienne valeur', visible: true, filter: 'text', width: '250px', frozen: false },
        { field: 'newValue', label: 'Nouvelle valeur', visible: true, filter: 'text', width: '250px', frozen: false },
        { field: 'organismeAuteur', label: 'Auteur - organisme', visible: false, filter: 'text', width: '250px', frozen: false },
        { field: 'nomAuteur', label: 'Auteur - utilisateur', visible: false, filter: 'text', width: '250px', frozen: false },
        { field: 'commentaire', label: 'Commentaire', visible: false, filter: 'text', width: '250px', frozen: false }
    ];

    /**
     * Ref to html modal component
     */
    @ViewChild('columnModal') columnModal: any;

    /**
     * Ref to the table
     */
    @ViewChild('dt') dt: Table;

    /**
     * List of modifications type
     */
    typeModifications: { name: string; code: string }[] = [];

    /**
     * Local storage key of columns visible in synthese volume of operator
     */
    readonly STORAGE_KEY = 'synthses_volume_operator_columns_visibility';

    constructor(
        private readonly operateurService: OperateursService,
        private readonly router: Router,
        private readonly modalService: NgbModal,
        private readonly datePipe: DatePipe,
        private readonly fb: FormBuilder,
        private readonly loaderService: LoaderService
    ) {
        // Récupère les modifications en fonction de la provenance via l'url
        const state = this.router.getCurrentNavigation() && this.router.getCurrentNavigation().extras.state;
        if (state) {
            if (state.operateur) {
                this.idOperateur = state.operateur;
            }
        }
    }

    /**
     * On init component
     */
    ngOnInit() {
        // Load data
        if (this.idOperateur) {
            this.loadModificationsOperateur(this.idOperateur);
        } else {
            this.loadModifications();
        }

        // Load columns visibility
        this.loadColumnVisibility();
    }

    /**
     * Récupère la liste des modifications filtrées sur l'opérateur
     * @param idOperateur - Id de l'opérateur
     * @param dateDebut - Date début de modification
     * @param dateFin - Date fin de modification
     */
    loadModificationsOperateur(idOperateur: number, dateDebut?: string, dateFin?: string) {
        this.operateurService
            .getHistoriqueModificationsOperateur(idOperateur, dateDebut, dateFin)
            .pipe(this.loaderService.operator())
            .subscribe(modifications => {
                this.assignDataLoaded(modifications);
            });
    }

    /**
     * Récupère l'ensemble des modifications des opérateurs de l'organisme connecté
     * @param dateDebut - Date début de modification
     * @param dateFin - Date fin de modification
     */
    loadModifications(dateDebut?: string, dateFin?: string) {
        this.operateurService
            .getOperateursByOrganisme(this.idOrganisme)
            .pipe(
                switchMap(operateurs => {
                    return this.operateurService.getHistoriqueModifications(
                        operateurs.map(op => op.id),
                        dateDebut,
                        dateFin
                    );
                }),
                this.loaderService.operator()
            )
            .subscribe(modifications => {
                this.assignDataLoaded(modifications);
            });
    }

    /**
     * Assign data loaded
     * @param modifications
     * @private
     */
    private assignDataLoaded(modifications: HistoriqueModification[]) {
        this.historique = modifications.map(h => {
            if (h.dateModification) {
                h.dateModification = new Date(h.dateModification);
            }
            return h;
        });
        this.typeModifications = [...new Set(this.historique.map(h => h.typeModification).sort((a, b) => a.localeCompare(b)))].map(rs => ({
            name: rs,
            code: rs
        }));
    }

    /**
     * Toggle column visibility
     * @param field - Column
     */
    toggleColumnVisibility(field: string): void {
        const col = this.columns.find(c => c.field === field);
        if (col) {
            col.visible = !col.visible;
            this.saveColumnVisibility();
        }
    }

    /**
     * Save columns visible
     */
    private saveColumnVisibility(): void {
        const visibility = this.columns.map(col => ({ field: col.field, visible: col.visible }));
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(visibility));
    }

    /**
     * Open column modal
     */
    openColumnModal() {
        this.modalService.open(this.columnModal, { centered: true });
    }

    /**
     * Get nb of columns visible
     */
    getVisibleColumnCount(): number {
        return this.columns.filter(col => col.visible).length;
    }

    /**
     * Load columns visible
     */
    loadColumnVisibility(): void {
        const saved = localStorage.getItem(this.STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            this.columns.forEach(col => {
                const savedCol = parsed.find((c: any) => c.field === col.field);
                if (savedCol) {
                    col.visible = savedCol.visible;
                }
            });
        }
    }

    /**
     * True if column visible
     * @param field - Column
     */
    isColumnVisible(field: string): boolean {
        const column = this.columns.find(c => c.field === field);
        return column ? column.visible : false;
    }

    /**
     * Récupère la valeur
     * @param hist - HistoriqueModification
     * @param field - Colonne
     */
    getCellValue(hist: HistoriqueModification, field: string) {
        if (field === 'nomAuteur' && hist.idOrganisme !== this.idOrganisme) {
            return 'Confidentiel';
        }
        if (field === 'dateModification') {
            return this.datePipe.transform(hist[field], 'dd/MM/yyyy');
        }
        return hist[field];
    }
}
